/**
 * Producer cloud library — saves the current script to the server and loads any of
 * the producer's saved scripts back. Server-side storage means scripts survive a
 * browser cache wipe, follow the producer across devices, and can be assisted by a
 * super-admin. Local (offline) projects still live in the device panel alongside.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Project } from '@goanna-im/shared';
import { deleteMyScript, listMyScripts, saveMyScript } from '../lib/library.js';
import type { StoredScript } from '../lib/library.js';

function ago(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function CloudLibraryPanel({
  project,
  onLoad,
}: {
  project: Project;
  onLoad: (p: Project) => void;
}) {
  const [scripts, setScripts] = useState<StoredScript[]>([]);
  const [status, setStatus] = useState('');

  const refresh = useCallback(async () => {
    try {
      setScripts(await listMyScripts());
    } catch (e) {
      setStatus(String((e as Error).message));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const saveCurrent = async () => {
    setStatus('Saving…');
    try {
      await saveMyScript(project);
      setStatus('Saved to cloud ✓');
      await refresh();
      setTimeout(() => setStatus(''), 1500);
    } catch (e) {
      setStatus(String((e as Error).message));
    }
  };

  const remove = async (s: StoredScript) => {
    if (!confirm(`Delete "${s.title}" from your cloud library?`)) return;
    try {
      await deleteMyScript(s.id);
      await refresh();
    } catch (e) {
      setStatus(String((e as Error).message));
    }
  };

  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>☁ Cloud library</h3>
        <button className="primary" onClick={saveCurrent}>Save current to cloud</button>
      </div>
      {status && <span className="tag readout">{status}</span>}
      {scripts.length === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
          Save the current script to keep it safe on the server — it&apos;ll be here on any device you
          sign in from.
        </p>
      ) : (
        scripts.map((s) => (
          <div key={s.id} className="row" style={{ justifyContent: 'space-between' }}>
            <span>
              <strong>{s.title || 'Untitled'}</strong>{' '}
              <span className="muted">· {s.blocks} blocks · {ago(s.updatedAt)}</span>
            </span>
            <span className="row" style={{ gap: '0.4rem' }}>
              <button onClick={() => onLoad(s.project)}>Load</button>
              <button className="danger" onClick={() => void remove(s)}>Delete</button>
            </span>
          </div>
        ))
      )}
    </section>
  );
}
